export interface MessageFeedback {
  reason?: string;
  errorType: string;
  subcategory?: string;
  correctedAnswer?: string;
}

interface EvalMessage {
  uuid: string;
  testChat: boolean;
  feedback: MessageFeedback;
  originalMessage: {
    uuid: string;
    status: string;
    owner: {
      name: string;
      phoneNumber: string | null;
    };
    timestamp: Date;
  };
}

interface ProposedResponse {
  uuid: string;
  body: string;
}

type EvalChatDetail = Omit<ChatDetail, 'messages'> & {
  messages: Array<MappedMessage>;
  proposedResponses?: Array<ProposedResponse>;
};

interface EvalMessagesResponse {
  fixed: EvalMessage[];
  unfixed: EvalMessage[];
}

export const useEvalStore = defineStore('evals', () => {
  const { $sellebrate } = useNuxtApp();

  const fixedEvalMessages = ref<EvalMessage[]>([]);
  const unfixedEvalMessages = ref<EvalMessage[]>([]);
  const chat = ref<EvalChatDetail | null>(null);
  const selectedEvalMessage = ref<EvalMessage | null>(null);
  const loadingEvalMessages = ref(false);

  const fetchEvalMessages = async (aiAgentId: string) => {
    loadingEvalMessages.value = true;
    try {
      const response = await $sellebrate<EvalMessagesResponse>(`/chats/evaluations/${aiAgentId}/messages`);

      fixedEvalMessages.value = response.fixed.map(evalMessage => ({
        ...evalMessage,
        originalMessage: {
          ...evalMessage.originalMessage,
          timestamp: new Date(evalMessage.originalMessage.timestamp),
        },
      }));

      unfixedEvalMessages.value = response.unfixed.map(evalMessage => ({
        ...evalMessage,
        originalMessage: {
          ...evalMessage.originalMessage,
          timestamp: new Date(evalMessage.originalMessage.timestamp),
        },
      }));
    }
    catch (error) {
      console.error('Failed to fetch eval messages', error);
    }
    finally {
      loadingEvalMessages.value = false;
    }
  };

  const fetchChat = async (uuid: string) => {
    let chatDetail: EvalChatDetail | null = null;
    try {
      chatDetail = await $sellebrate<EvalChatDetail | null>(`/chats/evaluations/chat/${uuid}`);
    }
    catch (error) {
      console.error('Failed to fetch chat details', error);
    }

    if (!chatDetail) return;

    chat.value = {
      ...chatDetail,
      messages: chatDetail.messages.map(message => ({
        ...message,
        timestamp: new Date(message.timestamp),
      })),
    };
  };

  const selectEvalMessage = (uuid: string) => {
    selectedEvalMessage.value
      = fixedEvalMessages.value.find(message => message.uuid === uuid)
        || unfixedEvalMessages.value.find(message => message.uuid === uuid)
        || null;

    if (selectedEvalMessage.value) {
      fetchChat(selectedEvalMessage.value.uuid);
    }
  };

  const evaluateChat = async (evaluationUuid: string, evalMessageUuid: string) => {
    try {
      const response = await $sellebrate<Array<ProposedResponse>>(
        `/chats/evaluations/${evalMessageUuid}/evaluate/${evaluationUuid}`,
        { method: 'get' },
      );

      // Check if response is an array
      if (!Array.isArray(response)) {
        console.error('Invalid response format:', response);
        return;
      }

      if (chat.value) {
        chat.value = {
          ...chat.value,
          proposedResponses: response.map(item => ({
            uuid: item.uuid,
            body: item.body,
          })),
        };
      }
    }
    catch (error) {
      console.error('Failed to evaluate chat:', error);
    }
  };

  const toggleFixed = async (evaluationUuid: string, evalMessageUuid: string) => {
    await $sellebrate(`/chats/evaluations/${evalMessageUuid}/toggle-fixed/${evaluationUuid}`, { method: 'put' });
  };

  const deleteEvaluation = async (evalMessageUuid: string, evaluationUuid: string) => {
    await $sellebrate(`/chats/evaluations/${evalMessageUuid}/delete/${evaluationUuid}`, { method: 'delete' });
    selectedEvalMessage.value = null;
  };

  return {
    chat,
    fetchChat,
    toggleFixed,
    evaluateChat,
    deleteEvaluation,
    fixedEvalMessages,
    fetchEvalMessages,
    selectEvalMessage,
    unfixedEvalMessages,
    selectedEvalMessage,
    loadingEvalMessages,
  };
});
